import type { BuilderItem, Tone } from "../types/gangsheet";

export const SHEET_WIDTH_IN = 22;
export const MIN_SHEET_LENGTH_IN = 24;
export const MAX_SHEET_LENGTH_IN = 164;
export const LENGTH_PRESETS = [24, 36, 48, 60, 72, 80];
export const MAX_UPLOADS = 50;
export const DEFAULT_SPACING_IN = 0.25;
export const PREVIEW_PX_PER_IN = 26;
export const EXPORT_DPI = 300;
export const MIN_ITEM_SIZE_IN = 0.5;

export function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

export function normalizeAngle(value: number) {
  let next = value % 360;
  if (next < 0) next += 360;
  return next;
}

export function inchesToPreviewPx(inches: number) {
  return inches * PREVIEW_PX_PER_IN;
}

export function previewPxToInches(px: number) {
  return px / PREVIEW_PX_PER_IN;
}

export function uuid() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function getUnrotatedRect(item: BuilderItem) {
  return {
    x: item.centerX - item.widthIn / 2,
    y: item.centerY - item.heightIn / 2,
    width: item.widthIn,
    height: item.heightIn,
  };
}

export function getRotatedCorners(item: BuilderItem) {
  const halfW = item.widthIn / 2;
  const halfH = item.heightIn / 2;
  const angle = (item.rotation * Math.PI) / 180;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);

  const points = [
    { x: -halfW, y: -halfH },
    { x: halfW, y: -halfH },
    { x: halfW, y: halfH },
    { x: -halfW, y: halfH },
  ];

  return points.map((point) => ({
    x: item.centerX + point.x * cos - point.y * sin,
    y: item.centerY + point.x * sin + point.y * cos,
  }));
}

export function getAxisAlignedBounds(item: BuilderItem) {
  const corners = getRotatedCorners(item);
  const xs = corners.map((point) => point.x);
  const ys = corners.map((point) => point.y);

  return {
    x: Math.min(...xs),
    y: Math.min(...ys),
    width: Math.max(...xs) - Math.min(...xs),
    height: Math.max(...ys) - Math.min(...ys),
  };
}

export function polygonsIntersect(
  polyA: { x: number; y: number }[],
  polyB: { x: number; y: number }[]
) {
  const polygons = [polyA, polyB];

  for (const polygon of polygons) {
    for (let i = 0; i < polygon.length; i += 1) {
      const next = (i + 1) % polygon.length;
      const edge = {
        x: polygon[next].x - polygon[i].x,
        y: polygon[next].y - polygon[i].y,
      };

      const axis = {
        x: -edge.y,
        y: edge.x,
      };

      let minA = Infinity;
      let maxA = -Infinity;

      for (const point of polyA) {
        const projected = point.x * axis.x + point.y * axis.y;
        minA = Math.min(minA, projected);
        maxA = Math.max(maxA, projected);
      }

      let minB = Infinity;
      let maxB = -Infinity;

      for (const point of polyB) {
        const projected = point.x * axis.x + point.y * axis.y;
        minB = Math.min(minB, projected);
        maxB = Math.max(maxB, projected);
      }

      if (maxA < minB || maxB < minA) {
        return false;
      }
    }
  }

  return true;
}

export function getDpi(item: BuilderItem) {
  const dpiW = item.naturalWidth / Math.max(item.widthIn, 0.001);
  const dpiH = item.naturalHeight / Math.max(item.heightIn, 0.001);
  return Math.floor(Math.min(dpiW, dpiH));
}

export function getDpiTone(dpi: number): Tone {
  if (dpi >= 300) {
    return {
      label: "Good",
      color: "#22c55e",
      bg: "rgba(34,197,94,0.14)",
      border: "rgba(34,197,94,0.3)",
    };
  }

  if (dpi >= 200) {
    return {
      label: "Warning",
      color: "#f59e0b",
      bg: "rgba(245,158,11,0.14)",
      border: "rgba(245,158,11,0.3)",
    };
  }

  return {
    label: "Low",
    color: "#ef4444",
    bg: "rgba(239,68,68,0.14)",
    border: "rgba(239,68,68,0.3)",
  };
}

export function clampCenterToSheet(item: BuilderItem, sheetLengthIn: number): BuilderItem {
  const bounds = getAxisAlignedBounds(item);

  let dx = 0;
  let dy = 0;

  if (bounds.x < 0) dx = -bounds.x;
  if (bounds.x + bounds.width > SHEET_WIDTH_IN) {
    dx = SHEET_WIDTH_IN - (bounds.x + bounds.width);
  }
  if (bounds.y < 0) dy = -bounds.y;
  if (bounds.y + bounds.height > sheetLengthIn) {
    dy = sheetLengthIn - (bounds.y + bounds.height);
  }

  return {
    ...item,
    centerX: item.centerX + dx,
    centerY: item.centerY + dy,
  };
}

export function autoNestItems(
  items: BuilderItem[],
  sheetLengthIn: number,
  spacingIn: number,
  allowRotate: boolean
): { items: BuilderItem[]; overflow: boolean } {
  const sorted = [...items]
    .map((item) => ({ ...item }))
    .sort((a, b) => {
      const areaA = a.widthIn * a.heightIn;
      const areaB = b.widthIn * b.heightIn;
      return areaB - areaA;
    });

  let currentX = spacingIn;
  let currentY = spacingIn;
  let rowHeight = 0;
  let overflow = false;

  const placed: BuilderItem[] = [];

  for (const original of sorted) {
    const item = { ...original };

    if (allowRotate) {
      const useQuarterTurn =
        item.widthIn > item.heightIn &&
        item.heightIn <= SHEET_WIDTH_IN / 2 &&
        normalizeAngle(item.rotation) % 180 === 0;

      if (useQuarterTurn) {
        item.rotation = 90;
      }
    } else {
      item.rotation = 0;
    }

    const boundsAtOrigin = getAxisAlignedBounds({
      ...item,
      centerX: 0,
      centerY: 0,
    });

    const itemWidth = boundsAtOrigin.width;
    const itemHeight = boundsAtOrigin.height;

    if (currentX + itemWidth > SHEET_WIDTH_IN - spacingIn) {
      currentX = spacingIn;
      currentY += rowHeight + spacingIn;
      rowHeight = 0;
    }

    const centeredItem = clampCenterToSheet(
      {
        ...item,
        centerX: currentX + itemWidth / 2,
        centerY: currentY + itemHeight / 2,
      },
      sheetLengthIn
    );

    placed.push(centeredItem);

    currentX += itemWidth + spacingIn;
    rowHeight = Math.max(rowHeight, itemHeight);

    const placedBounds = getAxisAlignedBounds(centeredItem);
    if (placedBounds.y + placedBounds.height > sheetLengthIn - spacingIn) {
      overflow = true;
    }
  }

  return { items: placed, overflow };
}

export function rotateSelectedByQuarterTurn(item: BuilderItem, sheetLengthIn: number) {
  return clampCenterToSheet(
    {
      ...item,
      rotation: normalizeAngle(item.rotation + 90),
    },
    sheetLengthIn
  );
}